import { horizonRoas, sortBy } from './campaigns-logic.js';
import { sortBy as sortRows } from './table-sort.js';
import { lastNDays, dayLabel, monthLabel, sixMonths, twelveWeeks, weekLabel, } from './period.js';
import { FACT_MIN_N, FORECAST_HORIZONS, factPoint, firstPendingFactFrom, horizonHasMatureFact, } from './fact-maturity.js';
import { groupVerdictLabel } from './verdict.js';
export { FACT_MIN_N, FORECAST_HORIZONS };
export const TREND_STEPS = ['day', 'week', 'month'];
export function readStep(v) {
    return TREND_STEPS.includes(v) ? v : 'week';
}
/** Период шага по умолчанию: 12 недель / 6 месяцев / 30 дней. */
export function stepDefaultPeriod(step, closedDay) {
    if (step === 'month')
        return sixMonths(closedDay);
    if (step === 'day')
        return lastNDays(30, closedDay);
    return twelveWeeks(closedDay);
}
/** Строки периодов шага; дни у старого auto-ads — `null`. */
export function periodRowsOf(report, step) {
    if (step === 'month')
        return report.periods.months;
    if (step === 'day')
        return report.periods.days ?? null;
    return report.periods.weeks;
}
/**
 * «22–28 июл», «Июль 2026», «20 окт»; неполный период — « · часть», у месяца
 * ещё и какие числа вошли: «Октябрь 2026 · часть, 1–19».
 */
export function rowLabel(row, step) {
    if (step === 'day')
        return dayLabel(row.from);
    if (step === 'week') {
        const base = weekLabel(row.from, row.to);
        return row.partial ? `${base} · часть` : base;
    }
    const base = monthLabel(row.key);
    if (!row.partial)
        return base;
    const a = Number(row.from.slice(8, 10));
    const b = Number(row.to.slice(8, 10));
    return `${base} · часть, ${a === b ? a : `${a}–${b}`}`;
}
const MONTH_ABBR = [
    'Янв',
    'Фев',
    'Мар',
    'Апр',
    'Май',
    'Июн',
    'Июл',
    'Авг',
    'Сен',
    'Окт',
    'Ноя',
    'Дек',
];
/**
 * Короткая подпись для узких мест (колонки фиксированной ширины, ось графика):
 * не длиннее ~10 знаков и без переноса. Месяц — «Сен 2026»; неполный период —
 * просто вошедшие дни («1–26 окт», «20–22 окт»). Полная подпись (`rowLabel`) —
 * в подсказке, aria, таблице периодов и CSV.
 */
export function shortRowLabel(row, step) {
    if (step === 'day')
        return dayLabel(row.from);
    if (row.partial || step === 'week')
        return weekLabel(row.from, row.to);
    const [y, m] = row.key.split('-').map(Number);
    return `${MONTH_ABBR[m - 1]} ${y}`;
}
/** В строке есть день с предварительным расходом. */
export function isPreliminaryRow(row, days) {
    return days.some((d) => d >= row.from && d <= row.to);
}
const usd = (minor) => (minor === null ? null : minor / 100);
export function chartPoints(rows, step) {
    return rows.map((r) => ({
        label: rowLabel(r, step),
        short: shortRowLabel(r, step),
        from: r.from,
        to: r.to,
        roas: r.group.answer.roas3,
        cpp: usd(r.group.cpp),
        be: usd(r.group.cppBreakevenMinor),
        spend: r.group.spendMinorFull / 100,
        n: r.group.purchases,
        hollow: r.group.answer.verdict === null || r.partial,
    }));
}
/** Диапазон оси с полями и шагом `step`, включая `extra` (порог). */
export function niceDomain(values, extra, step) {
    const xs = [...values, ...extra].filter((v) => typeof v === 'number' && Number.isFinite(v));
    if (xs.length === 0)
        return [0, step * 2];
    const lo = Math.max(0, Math.floor((Math.min(...xs) - step / 2) / step) * step);
    const hi = Math.ceil((Math.max(...xs) + step / 2) / step) * step;
    return [Math.round(lo * 1000) / 1000, Math.round(hi * 1000) / 1000];
}
/** Горизонты «Сбывается ли прогноз»: факт и прогноз за 60 / 90 / 120 дней. */
export const DEFAULT_FORECAST_HORIZON = '60';
/** Горизонт из адреса (`fh`); мусор — 60. */
export function readForecastHorizon(v) {
    return FORECAST_HORIZONS.includes(v)
        ? v
        : DEFAULT_FORECAST_HORIZON;
}
/**
 * Когда у горизонта появятся первые созревшие недели: правила те же, что в
 * «Как уточнялся прогноз» (`fact-maturity.ts`) — последний день недели + H + 3.
 * Нет недель с покупками без созревшего факта — `null`.
 */
export function horizonReadyFrom(weeks, h) {
    return firstPendingFactFrom(weeks.map((w) => ({ purchases: w.group.purchases, fact: factPoint(w, h) })));
}
/**
 * Срок «Сбывается ли прогноз» активен: есть неделя с созревшим фактом — то же
 * правило, что у «Точности» (`horizonHasMatureFact`).
 */
export function horizonAvailable(weeks, h) {
    return horizonHasMatureFact(weeks.map((w) => ({ fact: factPoint(w, h) })));
}
/** Корзин меньше 30 покупок — бледные (то же правило, что «Как уточнялся прогноз»). */
export const FEW_PURCHASES = 30;
/**
 * Гантели «Сбывается ли прогноз» по корзинам шага (день / неделя / месяц):
 * прогноз и факт за H дней по созревшим покупкам — оба без вычетов.
 */
export function dumbbells(weeks, h = DEFAULT_FORECAST_HORIZON, step = 'week') {
    const out = [];
    for (const w of weeks) {
        const f = w.group.factHorizons[h];
        if (!f || f.factN < FACT_MIN_N || f.factRoas === null || f.predRoasMatured === null)
            continue;
        if (f.predRoasMatured === 0)
            continue;
        const fp = factPoint(w, h);
        out.push({
            label: rowLabel(w, step),
            short: shortRowLabel(w, step),
            from: w.from,
            few: w.group.purchases < FEW_PURCHASES,
            pred: f.predRoasMatured,
            fact: f.factRoas,
            diff: f.factRoas / f.predRoasMatured - 1,
            mature: fp.state === 'mature',
            partialShare: fp.state === 'partial' ? fp.share : null,
        });
    }
    return out;
}
/** Колонки «Сбывается ли прогноз»: корзина, прогноз → факт (по факту), разница. */
export const FORECAST_SORT_KEYS = ['period', 'fact', 'diff'];
/** По умолчанию — по дате, от старой к новой (как пришли). */
export const DEFAULT_FORECAST_SORT = { key: 'period', dir: 'asc' };
export function sortDumbbells(list, sort) {
    return sortRows(list, sort, (d, k) => (k === 'period' ? d.from : k === 'fact' ? d.fact : d.diff));
}
/**
 * «Модель ошибалась на 11–21 %, и не в одну сторону» — ТОЛЬКО по созревшим
 * неделям (≥ 90 %); частичные в фразу не входят, у них звёздочка. Созревших
 * нет — `null`.
 */
export function dumbbellSummary(list) {
    const mature = list.filter((d) => d.mature);
    if (mature.length === 0)
        return null;
    const abs = mature.map((d) => Math.round(Math.abs(d.diff) * 100));
    const lo = Math.min(...abs);
    const hi = Math.max(...abs);
    const range = lo === hi ? `${lo}\u202F%` : `${lo}–${hi}\u202F%`;
    const signs = new Set(mature.map((d) => (d.diff >= 0 ? 1 : -1)));
    if (mature.length === 1)
        return `Модель ошиблась на ${range}.`;
    return signs.size > 1
        ? `Модель ошибалась на ${range}, и не в одну сторону.`
        : `Модель ошибалась на ${range}, в одну сторону.`;
}
/** Колонки, видимые по умолчанию (в этом порядке). */
export const PERIOD_BASE_COLUMNS = [
    'period',
    'spend',
    'purchases',
    'cpp',
    'breakeven',
    'roas',
    'verdict',
];
export const PERIOD_COLUMN_LABEL = {
    period: 'Период',
    spend: 'Расход',
    purchases: 'Покупки',
    cpp: 'Цена',
    breakeven: 'В ноль',
    roas: 'На 1 $',
    verdict: 'Вывод',
    fact: 'Уже вернулось',
    safe: 'Уверенно до',
    h60: '60 дн',
    h90: '90 дн',
    fact60: 'Факт 60',
    fact90: 'Факт 90',
    fact120: 'Факт 120',
    moment: 'Момент',
};
/** Полные подписи для меню «Колонки» (шапка — короткая, `PERIOD_COLUMN_LABEL`). */
const PERIOD_MENU_LABEL = {
    cpp: 'Цена покупки',
    breakeven: 'В ноль до',
    roas: 'Вернётся на 1 $',
    verdict: 'Вывод и шанс',
    moment: 'Момент прогноза',
};
/**
 * Колонки «Динамики» (одни и те же для дней, недель и месяцев — одно
 * хранилище): «Период» закреплён и всегда виден, справочные скрыты.
 */
export const PERIOD_COLUMN_DEFS = [
    ...PERIOD_BASE_COLUMNS,
    'fact',
    'safe',
    'h60',
    'h90',
    'fact60',
    'fact90',
    'fact120',
    'moment',
].map((id) => ({
    id,
    label: PERIOD_MENU_LABEL[id] ?? PERIOD_COLUMN_LABEL[id],
    ...(id === 'period' ? { defaultPin: 'left', hideable: false } : {}),
    ...(PERIOD_BASE_COLUMNS.includes(id) ? {} : { defaultVisible: false }),
}));
export const PERIOD_SORT_KEYS = PERIOD_COLUMN_DEFS.map((d) => d.id);
export const DEFAULT_PERIOD_SORT = { key: 'period', dir: 'desc' };
const VERDICT_RANK = { yes: 3, unclear: 2, no: 1 };
const MOMENT_RANK = { t0: 0, t24: 1, d14: 2, mixed: 3 };
/** «Факт H»: факт за H дней при n ≥ 5. */
export function factAt(row, h) {
    const f = row.group.factHorizons[h];
    return f && f.factN >= FACT_MIN_N && f.factRoas !== null
        ? { roas: f.factRoas, n: f.factN }
        : null;
}
/** «Факт 60»: факт 60 дней при n ≥ 5. */
export function fact60(row) {
    return factAt(row, '60');
}
/** Колонка «Факт H» → горизонт. */
export const FACT_COLUMN_HORIZON = {
    fact60: '60',
    fact90: '90',
    fact120: '120',
};
export function periodSortValue(row, key) {
    const g = row.group;
    switch (key) {
        case 'period':
            return row.from;
        case 'spend':
            return g.spendMinorFull;
        case 'purchases':
            return g.purchases;
        case 'cpp':
            return g.cpp;
        case 'breakeven':
            return g.cppBreakevenMinor;
        case 'roas':
            return g.answer.roas3;
        case 'verdict':
            return g.answer.verdict === null ? null : VERDICT_RANK[g.answer.verdict];
        case 'fact':
            return g.factRevenueMinor;
        case 'safe':
            return g.cppSafeMinor;
        case 'h60':
            return horizonRoas(g, '60');
        case 'h90':
            return horizonRoas(g, '90');
        case 'fact60':
        case 'fact90':
        case 'fact120':
            return factAt(row, FACT_COLUMN_HORIZON[key])?.roas ?? null;
        case 'moment':
            return MOMENT_RANK[row.momentUsed];
    }
}
export function sortPeriods(rows, sort) {
    return sortBy(rows, (r) => periodSortValue(r, sort.key), sort.dir);
}
export const MOMENT_SHORT = {
    t0: 'сразу',
    t24: 'через сутки',
    d14: 'на 14-й день',
    mixed: 'смешанный',
};
/** «через сутки · покрытие 100 %» и состав «сразу 0 · сутки 54 · 14 дн 0». */
export function momentText(row) {
    const cov = row.coveragePct === null ? '' : ` · покрытие ${Math.round(row.coveragePct)}\u202F%`;
    const mix = row.group.momentMix;
    return {
        main: `${MOMENT_SHORT[row.momentUsed]}${cov}`,
        mix: mix ? `сразу ${mix.t0 ?? 0} · сутки ${mix.t24 ?? 0} · 14 дн ${mix.d14 ?? 0}` : null,
    };
}
const dollars = (minor) => typeof minor === 'number' && Number.isFinite(minor) ? Math.round(minor) / 100 : null;
const round2 = (x) => typeof x === 'number' && Number.isFinite(x) ? Math.round(x * 100) / 100 : null;
export function periodCsvColumns(visible, step) {
    return visible.map((key) => {
        const money = ['spend', 'cpp', 'breakeven', 'safe', 'fact'].includes(key);
        const header = `${PERIOD_COLUMN_LABEL[key]}${money ? ', $' : ''}`;
        const value = (r) => {
            const g = r.group;
            switch (key) {
                case 'period':
                    return rowLabel(r, step);
                case 'spend':
                    return dollars(g.spendMinorFull);
                case 'purchases':
                    return g.purchases;
                case 'cpp':
                    return dollars(g.cpp);
                case 'breakeven':
                    return dollars(g.cppBreakevenMinor);
                case 'roas':
                    return round2(g.answer.roas3);
                case 'verdict':
                    return groupVerdictLabel(g);
                case 'fact':
                    return dollars(g.factRevenueMinor);
                case 'safe':
                    return dollars(g.cppSafeMinor);
                case 'h60':
                    return round2(horizonRoas(g, '60'));
                case 'h90':
                    return round2(horizonRoas(g, '90'));
                case 'fact60':
                case 'fact90':
                case 'fact120':
                    return round2(factAt(r, FACT_COLUMN_HORIZON[key])?.roas);
                case 'moment':
                    return momentText(r).main;
            }
        };
        return { header, value };
    });
}
//# sourceMappingURL=trend-logic.js.map